import { Dispatch } from "redux";
import { services } from "../services";
import { AxiosResponse, AxiosError } from "axios";

interface NFTImage {
  compressed?: string;
  original?: string;
}

interface AssetType {
  _id: string;
  name: string;
}

interface AssetCategoryRef {
  _id?: string;
  categoryName?: string | { en?: string | null };
  slugText?: string;
  name?: string;
}

interface NFTAsset {
  _id: string;
  symbol: string;
  image?: NFTImage;
  maxSupply: number;
  assetName: string;
  assetPrice: number;
  totalInvestorsCount?: number;
  totalInvestedAmount?: number;
  totalSupplyAmount: number;
  saleEndDate?: string;
  startTime?: string;
  assetAddress?: string;
  countryCode?: string;
  assetType?: AssetType;
  category?: AssetCategoryRef | AssetCategoryRef[];
  minPurchaseToken?: number | string;
  isFeatured?: boolean;
}

interface MappedNFTAsset {
  id: string;
  symbol: string;
  image: string | null;
  maxSupply: number;
  assetName: string;
  assetPrice: number;
  totalInvestors: number;
  totalInvestedAmount: number;
  totalSupplyAmount: number;
  endDate: string | null;
  startTime: string | null;
  assetAddress: string | undefined;
  countryCode?: string | null;
  assetType?: AssetType;
  category?: AssetCategoryRef | AssetCategoryRef[];
  minPurchaseToken?: number | string;
  isFeatured?: boolean;
}

interface CollectionUpdateParams {
  id: string;
  // Add other update parameters as needed
}

// Define the structure for action types
interface ActionTypes {
  FETCHED_NFT_BANNERS: string;
  FETCHED_INFO: string;
  FETCHED_HALL_FRAME_INFO: string;
  FETCHED_DASHBOARD: string;
  FETCHED_PAGINATION: string;
  FETCHED_CREATORS: string;
  FETCHED_MORE_CREATORS: string;
  FETCHED_MARKETPLACE: string;
  FETCHED_MORE_MARKETPLACE: string;
  FETCHED_COLLECTIONS: string;
  FETCHED_MORE_COLLECTIONS: string;
  FETCHED_COLLECTION_DETAIL: string;
  COLLECTION_UPDATED: string;
  FETCHED_TOP_NFT: string;
  FETCHED_TOP_COLLECTION: string;
  FETCHED_PROFILE_INFO: string;
  FETCHED_HALL_OF_FRAMES_ARTIST: string;
  FETCHED_HALL_OF_FRAMES_ARTWORK: string;
  FETCHED_HALL_OF_FRAMES_COLLECTOR: string;
  FETCHED_RENTAL_PROPERTIES: string;
  FETCHED_MORE_RENTAL_PROPERTIES: string;
  IS_LOADER: string;
}

// Action type constants
const ActionType: ActionTypes = {
  FETCHED_NFT_BANNERS: "FETCHED_NFT_BANNERS",
  FETCHED_INFO: "FETCHED_INFO",
  FETCHED_HALL_FRAME_INFO: "FETCHED_HALL_FRAME_INFO",
  FETCHED_DASHBOARD: "FETCHED_DASHBOARD",
  FETCHED_PAGINATION: "FETCHED_PAGINATION",
  FETCHED_CREATORS: "FETCHED_CREATORS",
  FETCHED_MORE_CREATORS: "FETCHED_MORE_CREATORS",
  FETCHED_MARKETPLACE: "FETCHED_MARKETPLACE",
  FETCHED_MORE_MARKETPLACE: "FETCHED_MORE_MARKETPLACE",
  FETCHED_COLLECTIONS: "FETCHED_COLLECTIONS",
  FETCHED_MORE_COLLECTIONS: "FETCHED_MORE_COLLECTIONS",
  FETCHED_COLLECTION_DETAIL: "FETCHED_COLLECTION_DETAIL",
  COLLECTION_UPDATED: "COLLECTION_UPDATED",
  FETCHED_TOP_NFT: "FETCHED_TOP_NFT",
  FETCHED_TOP_COLLECTION: "FETCHED_TOP_COLLECTION",
  FETCHED_PROFILE_INFO: "FETCHED_PROFILE_INFO",
  FETCHED_HALL_OF_FRAMES_ARTIST: "FETCHED_HALL_OF_FRAMES_ARTIST",
  FETCHED_HALL_OF_FRAMES_ARTWORK: "FETCHED_HALL_OF_FRAMES_ARTWORK",
  FETCHED_HALL_OF_FRAMES_COLLECTOR: "FETCHED_HALL_OF_FRAMES_COLLECTOR",
  FETCHED_RENTAL_PROPERTIES: "FETCHED_RENTAL_PROPERTIES",
  FETCHED_MORE_RENTAL_PROPERTIES: "FETCHED_MORE_RENTAL_PROPERTIES",
  IS_LOADER: "IS_LOADER",
};

// Define the action interface
interface Action<T> {
  type: string;
  data: T;
}

// Add these interfaces at the top of the file after the imports
interface ApiResponse<T> {
  data: T;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

interface Params {
  [key: string]: string | number | boolean;
}

// Helper function to create actions
function fetchedData<T>(type: string, data: T): Action<T> {
  return {
    type: type,
    data: data,
  };
}

// Helper function to handle API responses
function handleApiResponse<T>(
  promise: AxiosResponse<unknown> | AxiosError,
  dispatch: Dispatch,
  actionType: string
): void {
  if ("data" in promise && promise.data) {
    const response = promise.data as ApiResponse<T>;
    dispatch(fetchedData(actionType, response.data));
  }
}

// All the action functions
function fetchBanners() {
  return (dispatch: Dispatch) => {
    const response = services.get(`/admin/banner/list`, true);
    return response.then((promise: AxiosResponse<unknown> | AxiosError) => {
      handleApiResponse(promise, dispatch, ActionType.FETCHED_NFT_BANNERS);
    });
  };
}

function fetcInfo() {
  return (dispatch: Dispatch) => {
    const response = services.get(`/admin/info/list`, true);
    return response.then((promise: AxiosResponse<unknown> | AxiosError) => {
      handleApiResponse(promise, dispatch, ActionType.FETCHED_INFO);
    });
  };
}

function fetcHallFrameInfo() {
  return (dispatch: Dispatch) => {
    const response = services.get(`/admin/hall-frame-info/list`, true);
    return response.then((promise: AxiosResponse<unknown> | AxiosError) => {
      handleApiResponse(promise, dispatch, ActionType.FETCHED_HALL_FRAME_INFO);
    });
  };
}

function fetchDashboardConfig() {
  return (dispatch: Dispatch) => {
    const response = services.get(`/admin/dashboard/list`, true);
    return response.then((promise: AxiosResponse<unknown> | AxiosError) => {
      handleApiResponse(promise, dispatch, ActionType.FETCHED_DASHBOARD);
    });
  };
}

function getCreators(params: Params = {}) {
  return async (dispatch: Dispatch) => {
    const response = services.post(`user/listVerifiefCreator`, params);
    response.then((promise: AxiosResponse<unknown> | AxiosError) => {
      if ("data" in promise && promise.status === 200) {
        dispatch(
          fetchedData(
            ActionType.FETCHED_PAGINATION,
            (promise.data as ApiResponse<unknown>).pagination
          )
        );
        dispatch(
          fetchedData(
            ActionType.FETCHED_CREATORS,
            (promise.data as ApiResponse<unknown>).data
          )
        );
      }
    });
  };
}

function getMoreCreators(params: Params = {}) {
  return async (dispatch: Dispatch) => {
    const response = services.post(`user/listVerifiefCreator`, params);
    response.then((promise: AxiosResponse<unknown> | AxiosError) => {
      if ("data" in promise && promise.status === 200) {
        dispatch(
          fetchedData(
            ActionType.FETCHED_PAGINATION,
            (promise.data as ApiResponse<unknown>).pagination
          )
        );
        dispatch(
          fetchedData(
            ActionType.FETCHED_MORE_CREATORS,
            (promise.data as ApiResponse<unknown>).data
          )
        );
      }
    });
  };
}

function getRentalProperties(params: Params = {}) {
  return async (dispatch: Dispatch) => {
    dispatch(fetchedData("IS_LOADER", { isLoading: true }));
    const response = services.get(`rental/getRentalList`, true, params);
    response
      .then((promise: AxiosResponse<unknown> | AxiosError) => {
        if ("data" in promise && promise.status === 200) {
          dispatch(
            fetchedData(
              ActionType.FETCHED_PAGINATION,
              (promise.data as ApiResponse<unknown>).pagination
            )
          );
          dispatch(
            fetchedData(
              ActionType.FETCHED_RENTAL_PROPERTIES,
              (promise.data as ApiResponse<unknown>).data
            )
          );
        }
        dispatch(fetchedData("IS_LOADER", { isLoading: false }));
      })
      .catch(() => {
        dispatch(fetchedData("IS_LOADER", { isLoading: false }));
      });
  };
}

function getMarketPlaceNFT(params: Params = {}) {
  return async (dispatch: Dispatch) => {
    dispatch(fetchedData("IS_LOADER", { isLoading: true }));
    const response = services.get(`asset/user`, true, params);
    response
      .then((promise: AxiosResponse<unknown> | AxiosError) => {
        if ("data" in promise && promise.status === 200) {
          const response = promise.data as ApiResponse<NFTAsset[]>;
          const mappedData: MappedNFTAsset[] = response.data.map(
            (item: NFTAsset) => ({
              id: item._id,
              symbol: item.symbol,
              image: item.image?.compressed || item.image?.original || null,
              maxSupply: item.maxSupply,
              assetName: item.assetName,
              assetPrice: item.assetPrice,
              totalInvestors: item.totalInvestorsCount || 0,
              totalInvestedAmount: item.totalInvestedAmount || 0,
              totalSupplyAmount: item.totalSupplyAmount,
              endDate: item.saleEndDate || null,
              startTime: item.startTime || null,
              assetAddress: item.assetAddress,
              countryCode: item.countryCode || null,
              assetType: item.assetType,
              category: item.category,
              minPurchaseToken: item.minPurchaseToken,
              isFeatured: item.isFeatured === true,
            })
          );
          dispatch(
            fetchedData(ActionType.FETCHED_PAGINATION, response.pagination)
          );
          dispatch(fetchedData(ActionType.FETCHED_MARKETPLACE, mappedData));
          dispatch(fetchedData("IS_LOADER", { isLoading: false }));
        }
      })
      .catch((error) => {
        console.error("Error fetching marketplace NFTs:", error);
        dispatch(fetchedData("IS_LOADER", { isLoading: false }));
      });
  };
}

function getMoreMarketPlaceNFT(params: Params = {}) {
  return async (dispatch: Dispatch) => {
    dispatch(fetchedData("IS_LOADER", { isLoading: true }));
    const response = services.get(`asset/user`, true, params);
    response
      .then((promise: AxiosResponse<unknown> | AxiosError) => {
        if ("data" in promise && promise.status === 200) {
          const response = promise.data as ApiResponse<NFTAsset[]>;
          const mappedData: MappedNFTAsset[] = response.data.map(
            (item: NFTAsset) => ({
              id: item._id,
              symbol: item.symbol,
              image: item.image?.compressed || item.image?.original || null,
              maxSupply: item.maxSupply,
              assetName: item.assetName,
              assetPrice: item.assetPrice,
              totalInvestors: item.totalInvestorsCount || 0,
              totalInvestedAmount: item.totalInvestedAmount || 0,
              totalSupplyAmount: item.totalSupplyAmount,
              endDate: item.saleEndDate || null,
              startTime: item.startTime || null,
              assetAddress: item.assetAddress,
              countryCode: item.countryCode || null,
              assetType: item.assetType,
              category: item.category,
              minPurchaseToken: item.minPurchaseToken,
              isFeatured: item.isFeatured === true,
            })
          );
          dispatch(
            fetchedData(ActionType.FETCHED_PAGINATION, response.pagination)
          );
          dispatch(
            fetchedData(ActionType.FETCHED_MORE_MARKETPLACE, mappedData)
          );
          dispatch(fetchedData("IS_LOADER", { isLoading: false }));
        }
      })
      .catch((error) => {
        console.error("Error fetching more marketplace NFTs:", error);
        dispatch(fetchedData("IS_LOADER", { isLoading: false }));
      });
  };
}

function getMoreRentalMarketPlaceNFT(params: Params = {}) {
  return async (dispatch: Dispatch) => {
    const response = services.get(`rental/getRentalList`, true, params);
    response
      .then((promise: AxiosResponse<unknown> | AxiosError) => {
        if ("data" in promise && promise.status === 200) {
          dispatch(
            fetchedData(
              ActionType.FETCHED_PAGINATION,
              (promise.data as ApiResponse<unknown>).pagination
            )
          );
          dispatch(
            fetchedData(
              ActionType.FETCHED_MORE_RENTAL_PROPERTIES,
              (promise.data as ApiResponse<unknown>).data
            )
          );
        }
      })
      .catch((error) => {
        console.error("Error fetching more rental properties:", error);
      });
  };
}

function filterRentalProperties(params: Params = {}) {
  return async (dispatch: Dispatch) => {
    console.log("🚀 filterRentalProperties action called with params:", params);
    dispatch(fetchedData("IS_LOADER", { isLoading: true }));
    const response = services.get(`rental/filter`, true, params);
    response
      .then((promise: AxiosResponse<unknown> | AxiosError) => {
        if ("data" in promise && promise.status === 200) {
          console.log("✅ Filter response received:", promise.data);
          dispatch(
            fetchedData(
              ActionType.FETCHED_PAGINATION,
              (promise.data as ApiResponse<unknown>).pagination
            )
          );
          dispatch(
            fetchedData(
              ActionType.FETCHED_RENTAL_PROPERTIES,
              (promise.data as ApiResponse<unknown>).data
            )
          );
          dispatch(fetchedData("IS_LOADER", { isLoading: false }));
        }
      })
      .catch((error) => {
        console.error("Error filtering rental properties:", error);
        dispatch(fetchedData("IS_LOADER", { isLoading: false }));
      });
  };
}

function getCollections(params: Params = {}) {
  return async (dispatch: Dispatch) => {
    const response = services.get(`asset/user`, true, params);
    response.then((promise: AxiosResponse<unknown> | AxiosError) => {
      if ("data" in promise && promise.status === 200) {
        dispatch(
          fetchedData(
            ActionType.FETCHED_PAGINATION,
            (promise.data as ApiResponse<unknown>).pagination
          )
        );
        dispatch(
          fetchedData(
            ActionType.FETCHED_COLLECTIONS,
            (promise.data as ApiResponse<unknown>).data
          )
        );
      }
    });
  };
}

function getMoreCollections(params: Params = {}) {
  return async (dispatch: Dispatch) => {
    const response = services.get(`asset/user`, true, params);
    response.then((promise: AxiosResponse<unknown> | AxiosError) => {
      if ("data" in promise && promise.status === 200) {
        dispatch(
          fetchedData(
            ActionType.FETCHED_PAGINATION,
            (promise.data as ApiResponse<unknown>).pagination
          )
        );
        dispatch(
          fetchedData(
            ActionType.FETCHED_MORE_COLLECTIONS,
            (promise.data as ApiResponse<unknown>).data
          )
        );
      }
    });
  };
}

function getCollectionDetails(params: { id: string }) {
  return async (dispatch: Dispatch) => {
    const response = services.get(`nft/getCollectionInfo/${params.id}`, true);
    response.then((promise: AxiosResponse<unknown> | AxiosError) => {
      if ("data" in promise && promise.status === 200) {
        dispatch(
          fetchedData(
            ActionType.FETCHED_COLLECTION_DETAIL,
            (promise.data as ApiResponse<unknown>).data
          )
        );
      }
    });
  };
}

function updateCollection(params: CollectionUpdateParams) {
  return async (dispatch: Dispatch) => {
    const response = services.put(`nft/updateCollection/${params.id}`, params);
    response.then((promise: AxiosResponse<unknown> | AxiosError) => {
      if ("data" in promise && promise.status === 200) {
        dispatch(fetchedData(ActionType.COLLECTION_UPDATED, promise.data));
      }
    });
  };
}

function getTopNFT() {
  return (dispatch: Dispatch) => {
    const response = services.get(`/admin/popular/list`, true);
    return response.then((promise: AxiosResponse<unknown> | AxiosError) => {
      handleApiResponse(promise, dispatch, ActionType.FETCHED_TOP_NFT);
    });
  };
}

function getTopCollection() {
  return (dispatch: Dispatch) => {
    const response = services.get(`/admin/popularCollection/list`, true);
    return response.then((promise: AxiosResponse<unknown> | AxiosError) => {
      handleApiResponse(promise, dispatch, ActionType.FETCHED_TOP_COLLECTION);
    });
  };
}

function getProfileInfo() {
  return (dispatch: Dispatch) => {
    const response = services.get(`/admin/profile-info/list`, true);
    return response.then((promise: AxiosResponse<unknown> | AxiosError) => {
      handleApiResponse(promise, dispatch, ActionType.FETCHED_PROFILE_INFO);
    });
  };
}

function getHallOfFrameArtist() {
  return (dispatch: Dispatch) => {
    const response = services.get(`/hallOfFrame/list/artist`, true);
    return response.then((promise: AxiosResponse<unknown> | AxiosError) => {
      handleApiResponse(
        promise,
        dispatch,
        ActionType.FETCHED_HALL_OF_FRAMES_ARTIST
      );
    });
  };
}

function getHallOfFrameArtwork() {
  return (dispatch: Dispatch) => {
    const response = services.get(`/hallOfFrame/list/artwork`, true);
    return response.then((promise: AxiosResponse<unknown> | AxiosError) => {
      handleApiResponse(
        promise,
        dispatch,
        ActionType.FETCHED_HALL_OF_FRAMES_ARTWORK
      );
    });
  };
}

function getHallOfFrameCollector() {
  return (dispatch: Dispatch) => {
    const response = services.get(`/hallOfFrame/list/collector`, true);
    return response.then((promise: AxiosResponse<unknown> | AxiosError) => {
      handleApiResponse(
        promise,
        dispatch,
        ActionType.FETCHED_HALL_OF_FRAMES_COLLECTOR
      );
    });
  };
}

function authenticateUser() {
  return (dispatch: Dispatch) => {
    const response = services.get(`/auth/user`, true);
    return response.then((promise: AxiosResponse<unknown> | AxiosError) => {
      handleApiResponse(promise, dispatch, ActionType.FETCHED_PROFILE_INFO);
    });
  };
}

// Export the auth actions object
export const authActions = {
  fetchBanners,
  fetcInfo,
  fetcHallFrameInfo,
  fetchDashboardConfig,
  getCreators,
  getMoreCreators,
  getMarketPlaceNFT,
  getMoreMarketPlaceNFT,
  getCollections,
  getCollectionDetails,
  getMoreCollections,
  updateCollection,
  getTopNFT,
  getTopCollection,
  getProfileInfo,
  getHallOfFrameArtist,
  getHallOfFrameArtwork,
  getHallOfFrameCollector,
  getRentalProperties,
  authenticateUser,
  getMoreRentalMarketPlaceNFT,
  filterRentalProperties,
};
